import React, { useMemo } from "react";

const PdfReportLayout = React.forwardRef((props, ref) => {
  const {
    /* --- nuovi props --------------------------------------------- */
    hideDates = false,          // se true, non mostra date/orari
    /* --- props esistenti ----------------------------------------- */
    activity,
    mapPosition,
    startDateTime,
    endDateTime,
    creatorUser,
    assignedUser,
    getLabelFromValue,
    ACTIVITY_OPTIONS,
    MEANS_OPTIONS,
    getUploadUrl,
    MapComponent,
  } = props;

  /* --------------------------------------------------------------- */
  /*                URL immagini prima / dopo                       */
  /* --------------------------------------------------------------- */
  const beforeUrls = useMemo(() => {
    if (!activity.photo_before || activity.photo_before === "null") return [];
    return Array.isArray(activity.photo_before)
      ? activity.photo_before.map(id => getUploadUrl(id))
      : [getUploadUrl(activity.photo_before)];
  }, [activity.photo_before, getUploadUrl]);

  const afterUrls = useMemo(() => {
    if (!activity.photo_after || activity.photo_after === "null") return [];
    return Array.isArray(activity.photo_after)
      ? activity.photo_after.map(id => getUploadUrl(id))
      : [getUploadUrl(activity.photo_after)];
  }, [activity.photo_after, getUploadUrl]);

  // Funzione per creare link compatibili - restituisce SEMPRE l'URL come viene da getUploadUrl
  const createShortLink = (fullUrl, index, type) => {
    // getUploadUrl ritorna già l'URL corretto nel formato BASE_URL/uploads/filename
    // Dobbiamo solo assicurarci che sia completo con protocollo e host

    // Se l'URL ha già il protocollo, è completo
    if (fullUrl.startsWith('http://') || fullUrl.startsWith('https://')) {
      return fullUrl;
    }

    // Se inizia con //, aggiungi solo il protocollo
    if (fullUrl.startsWith('//')) {
      return window.location.protocol + fullUrl;
    }

    // Se è un percorso relativo che inizia con /
    if (fullUrl.startsWith('/')) {
      // Usa l'host corrente o quello da env
      let baseUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin;

      // Assicurati che baseUrl abbia il protocollo
      if (!baseUrl.startsWith('http://') && !baseUrl.startsWith('https://')) {
        baseUrl = window.location.protocol + '//' + baseUrl;
      }

      // Rimuovi /api/v0 se presente alla fine del baseUrl
      baseUrl = baseUrl.replace(/\/api\/v0\/?$/, '');

      // Rimuovi trailing slash
      baseUrl = baseUrl.replace(/\/+$/, '');

      // Restituisci URL completo
      return baseUrl + fullUrl;
    }

    // Fallback: costruisci URL con uploads
    const fileName = fullUrl.split('/').pop();
    let baseUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin;

    if (!baseUrl.startsWith('http://') && !baseUrl.startsWith('https://')) {
      baseUrl = window.location.protocol + '//' + baseUrl;
    }

    baseUrl = baseUrl.replace(/\/api\/v0\/?$/, '').replace(/\/+$/, '');
    return `${baseUrl}/uploads/${fileName}`;
  };

  const capitalizeFirst = str => str.charAt(0).toUpperCase() + str.slice(1);

  const formatDateWithWeekday = dateStr => {
    const d = new Date(dateStr);
    const wd = d.toLocaleDateString("it-IT", { weekday: "short" });
    return `${capitalizeFirst(wd)}, ${String(d.getDate()).padStart(2, "0")}`;
  };

  const formatLocation = info => {
    if (!info) return "-";
    const parts = info.split(",").map(p => p.trim());
    const civico = parts[0] || "";
    const via    = parts[1] || "";
    const città  = parts[4] || "";
    return `${via} ${civico}, ${città}`;
  };

  /* --------------------------------------------------------------- */
  /*                            LAYOUT                               */
  /* --------------------------------------------------------------- */
  return (
    <div
      ref={ref}
      style={{
        position: "absolute",
        top: "-10000px",
        left: "-10000px",
        width: "1000px",
        backgroundColor: "#fff",
        padding: "20px",
      }}
    >
      {/* ------------------- MAPPA & LINK FOTO -------------------- */}
      <div style={{ marginBottom: "20px" }}>
        <div
          style={{
            maxWidth: "1000px",
            margin: "0 auto",
            maxHeight: "400px",
            overflow: "hidden",
            borderRadius: "1rem",
          }}
        >
          <MapComponent
            position={mapPosition}
            setPosition={() => {}}
            onLocationInfo={() => {}}
          />
        </div>

        {/* Link foto */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "20px",
            gap: "20px",
            flexWrap: "wrap",
          }}
        >
          {/* FOTO PRIMA */}
          <div style={{ flex: "1", minWidth: "300px" }}>
            <label style={{ fontWeight: "bold" }}>Link Foto Prima:</label>
            <div style={{ marginTop: "8px", fontSize: "14px" }}>
              {beforeUrls.length ? (
                beforeUrls.map((url, i) => {
                  const shortLink = createShortLink(url, i, 'before');
                  const fileName = url.split('/').pop();
                  return (
                    <div key={i} style={{ marginBottom: "15px" }}>
                      <div
                        className="pdf-link-area"
                        data-link-url={shortLink}
                        data-link-index={`prima-${i}`}
                        style={{
                          backgroundColor: "#e3f2fd",
                          padding: "8px 12px",
                          borderRadius: "4px",
                          border: "1px solid #1976d2",
                          cursor: "pointer"
                        }}
                      >
                        <div style={{
                          color: "#0000FF",
                          fontSize: "13px",
                          fontWeight: "bold",
                          textDecoration: "underline"
                        }}>
                          {`📷 Foto Prima ${i + 1} - CLICCA QUI`}
                        </div>
                        <div style={{
                          fontSize: "10px",
                          color: "#555",
                          marginTop: "4px"
                        }}>
                          {fileName}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <span style={{ color: "#666" }}>Nessuna foto prima</span>
              )}
            </div>
          </div>

          {/* FOTO DOPO */}
          <div style={{ flex: "1", minWidth: "300px" }}>
            <label style={{ fontWeight: "bold" }}>Link Foto Dopo:</label>
            <div style={{ marginTop: "8px", fontSize: "14px" }}>
              {afterUrls.length ? (
                afterUrls.map((url, i) => {
                  const shortLink = createShortLink(url, i, 'after');
                  const fileName = url.split('/').pop();
                  return (
                    <div key={i} style={{ marginBottom: "15px" }}>
                      <div
                        className="pdf-link-area"
                        data-link-url={shortLink}
                        data-link-index={`dopo-${i}`}
                        style={{
                          backgroundColor: "#e3f2fd",
                          padding: "8px 12px",
                          borderRadius: "4px",
                          border: "1px solid #1976d2",
                          cursor: "pointer"
                        }}
                      >
                        <div style={{
                          color: "#0000FF",
                          fontSize: "13px",
                          fontWeight: "bold",
                          textDecoration: "underline"
                        }}>
                          {`📷 Foto Dopo ${i + 1} - CLICCA QUI`}
                        </div>
                        <div style={{
                          fontSize: "10px",
                          color: "#555",
                          marginTop: "4px"
                        }}>
                          {fileName}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <span style={{ color: "#666" }}>Nessuna foto dopo</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* --------------------- DETTAGLI GRID ----------------------- */}
      <div
        style={{
          backgroundColor: "#fff",
          color: "#1c1c1c",
          padding: "20px",
          borderRadius: "1rem",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "20px",
          }}
        >
          {/* Rig.1: Attività / Località */}
          <div>
            <label style={{ fontWeight: "bold" }}>Attività:</label>
            <div style={{ marginTop: "8px", fontSize: "14px" }}>
              {activity.activity_names?.map((n, i) => (
                <span key={i} style={{ marginRight: "4px" }}>
                  {getLabelFromValue(n, ACTIVITY_OPTIONS)}
                </span>
              ))}
            </div>
          </div>
          <div>
            {activity.location_info && (
              <>
                <label style={{ fontWeight: "bold" }}>Località:</label>
                <div style={{ marginTop: "8px", fontSize: "14px" }}>
                  📍 {formatLocation(activity.location_info)}
                </div>
              </>
            )}
          </div>

          {/* Rig.2: Mezzo / Priorità */}
          <div>
            <label style={{ fontWeight: "bold" }}>Mezzo:</label>
            <div style={{ marginTop: "8px", fontSize: "14px" }}>
              {activity.means?.map((m, i) => (
                <span key={i} style={{ marginRight: "4px" }}>
                  {getLabelFromValue(m, MEANS_OPTIONS)}
                </span>
              ))}
            </div>
          </div>
          <div>
            <label style={{ fontWeight: "bold" }}>Priorità:</label>
            <div style={{ marginTop: "8px", fontSize: "14px" }}>
              {activity.priority}
            </div>
          </div>

          {/* Rig.3: Assegnato da / a */}
          <div>
            <label style={{ fontWeight: "bold" }}>Assegnato da:</label>
            <div style={{ marginTop: "8px", fontSize: "14px" }}>
              {creatorUser
                ? `${creatorUser.firstName} ${creatorUser.lastName}`
                : "Caricamento..."}
            </div>
          </div>
          <div>
            <label style={{ fontWeight: "bold" }}>Assegnato a:</label>
            <div style={{ marginTop: "8px", fontSize: "14px" }}>
              {assignedUser
                ? `${assignedUser.firstName} ${assignedUser.lastName}`
                : "Caricamento..."}
            </div>
          </div>

          {/* Rig.4: Periodo (nascosto se hideDates) */}
          {!hideDates && (
            <div style={{ gridColumn: "span 2" }}>
              <label style={{ fontWeight: "bold" }}>Periodo:</label>
              <div style={{ marginTop: "8px", fontSize: "14px" }}>
                <div>
                  Inizio:{" "}
                  {(startDateTime || new Date("0000-01-01")).toLocaleDateString(
                    "it-IT",
                    { day: "2-digit", month: "2-digit" }
                  )}
                </div>
                <div>
                  Fine:{" "}
                  {(endDateTime || new Date("0000-01-01")).toLocaleDateString(
                    "it-IT",
                    { day: "2-digit", month: "2-digit" }
                  )}
                </div>
                <div>
                  Anno: {startDateTime ? startDateTime.getFullYear() : "0000"}
                </div>
              </div>
            </div>
          )}

          {/* Rig.5: Descrizione */}
          <div style={{ gridColumn: "span 2" }}>
            <label style={{ fontWeight: "bold" }}>Descrizione:</label>
            <div
              style={{
                marginTop: "8px",
                border: "1px solid #1c1c1c",
                padding: "10px",
                borderRadius: "0.5rem",
                minHeight: "150px",
                fontSize: "14px",
              }}
            >
              {activity.description}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default PdfReportLayout;
